import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { Upload, X, ImageIcon } from 'lucide-react';
import { Button } from '@edusense/shared-ui-kit';
// --- Styled Components ---
const FormContainer = styled.form`
  background-color: #fff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 600px; 
  margin: 20px auto; 
`;
const FormGroup = styled.div`
  margin-bottom: 20px;
`;
const Label = styled.label`
  display: block; 
  margin-bottom: 8px; 
  font-weight: bold; 
  font-size: 0.9rem;
`;
const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
`;
const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
  min-height: 120px; 
  resize: vertical;
`;
const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
  background-color: white;
`;
const SubmitButton = styled.button`
  background-color: #007bff; 
  color: white; 
  padding: 12px 20px; 
  border: none;
  border-radius: 4px; 
  font-size: 1rem; 
  font-weight: bold; 
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #0056b3;
  }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;
const FileInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const FileInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #333;
  flex-grow: 1;
  min-width: 0;
`;
const FileName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ThumbnailPreview = styled.img`
  display: block;
  max-width: 150px;
  max-height: 80px;
  margin-top: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
`;
const TextSmall = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
`;
/**
 * 강의 편집 폼 컴포넌트
 * 강의 정보를 생성하거나 수정하는 폼
 */ const toUrlString = (value)=>{
    if (!value) return null;
    return typeof value === 'string' ? value : value.toString();
};
const CourseEditForm = ({ initialData, onSubmit, isLoading })=>{
    const initialThumbnail = toUrlString(initialData?.thumbnailUrl);
    const [formData, setFormData] = useState({
        title: initialData?.title ?? '',
        description: initialData?.description ?? null,
        level: initialData?.level ?? CreateCourseDraftBodyLevel.BEGINNER,
        price: initialData?.price ?? null,
        sections: initialData?.sections ?? null,
        thumbnailFile: null,
        existingThumbnailUrl: initialThumbnail
    });
    const thumbnailInputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(initialThumbnail);
    useEffect(()=>{
        const normalizedThumbnail = toUrlString(initialData?.thumbnailUrl);
        setFormData({
            title: initialData?.title ?? '',
            description: initialData?.description ?? null,
            level: initialData?.level ?? CreateCourseDraftBodyLevel.BEGINNER,
            price: initialData?.price ?? null,
            sections: initialData?.sections ?? null,
            thumbnailFile: null,
            existingThumbnailUrl: normalizedThumbnail
        });
        setPreviewUrl(normalizedThumbnail);
    }, [
        initialData
    ]);
    useEffect(()=>{
        let objectUrl;
        if (formData.thumbnailFile) {
            objectUrl = URL.createObjectURL(formData.thumbnailFile);
            setPreviewUrl(objectUrl);
        } else {
            setPreviewUrl(formData.existingThumbnailUrl ?? null);
        }
        return ()=>{
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [
        formData.thumbnailFile,
        formData.existingThumbnailUrl
    ]);
    const handleChange = (e)=>{
        const { name, value, files } = e.target;
        if (name === 'thumbnailFile' && files && files.length > 0) {
            setFormData((prev)=>({
                    ...prev,
                    thumbnailFile: files[0],
                    existingThumbnailUrl: null
                }));
            if (thumbnailInputRef.current) {
                thumbnailInputRef.current.value = '';
            }
        } else if (name !== 'thumbnailFile') {
            setFormData((prev)=>({
                    ...prev,
                    [name]: value
                }));
        }
    };
    const handleRemoveThumbnail = ()=>{
        setFormData((prev)=>({
                ...prev,
                thumbnailFile: null,
                existingThumbnailUrl: null
            }));
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.value = '';
        }
    };
    const triggerThumbnailUpload = ()=>{
        thumbnailInputRef.current?.click();
    };
    const handleSubmit = (e)=>{
        e.preventDefault();
        if (!formData.title) {
            alert('강의 제목을 입력해주세요.');
            return;
        }
        onSubmit(formData);
    };
    return /*#__PURE__*/ _jsxs(FormContainer, {
        onSubmit: handleSubmit,
        children: [
            /*#__PURE__*/ _jsx("h2", {
                children: initialData ? '강의 정보 수정' : '새 강의 정보 입력'
            }),
            /*#__PURE__*/ _jsxs(FormGroup, {
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        htmlFor: "title",
                        children: "강의 제목"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        type: "text",
                        id: "title",
                        name: "title",
                        value: formData.title,
                        onChange: handleChange,
                        required: true
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(FormGroup, {
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        htmlFor: "description",
                        children: "강의 설명"
                    }),
                    /*#__PURE__*/ _jsx(TextArea, {
                        id: "description",
                        name: "description",
                        value: formData.description || '',
                        onChange: handleChange
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(FormGroup, {
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        htmlFor: "level",
                        children: "난이도"
                    }),
                    /*#__PURE__*/ _jsxs(Select, {
                        id: "level",
                        name: "level",
                        value: formData.level || CreateCourseDraftBodyLevel.BEGINNER,
                        onChange: handleChange,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: CreateCourseDraftBodyLevel.BEGINNER,
                                children: "초급"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: CreateCourseDraftBodyLevel.INTERMEDIATE,
                                children: "중급"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: CreateCourseDraftBodyLevel.ADVANCED,
                                children: "고급"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: CreateCourseDraftBodyLevel.ALL,
                                children: "모든 레벨"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(FormGroup, {
                children: [
                    /*#__PURE__*/ _jsx(Label, {
                        children: "썸네일 이미지"
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        type: "file",
                        ref: thumbnailInputRef,
                        name: "thumbnailFile",
                        accept: "image/*",
                        onChange: handleChange,
                        style: {
                            display: 'none'
                        }
                    }),
                    /*#__PURE__*/ _jsxs(FileInputWrapper, {
                        children: [
                            /*#__PURE__*/ _jsxs(Button, {
                                type: "button",
                                variant: "outline",
                                onClick: triggerThumbnailUpload,
                                size: "sm",
                                children: [
                                    /*#__PURE__*/ _jsx(Upload, {
                                        className: "h-4 w-4 mr-2"
                                    }),
                                    "파일 선택"
                                ]
                            }),
                            previewUrl && /*#__PURE__*/ _jsxs(FileInfoWrapper, {
                                children: [
                                    /*#__PURE__*/ _jsx(ImageIcon, {
                                        className: "h-5 w-5 text-gray-500 flex-shrink-0"
                                    }),
                                    /*#__PURE__*/ _jsx(FileName, {
                                        children: formData.thumbnailFile ? formData.thumbnailFile.name : '기존 이미지 사용 중'
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        type: "button",
                                        variant: "ghost",
                                        onClick: handleRemoveThumbnail,
                                        className: "ml-auto flex-shrink-0",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-4 w-4"
                                        })
                                    })
                                ]
                            }),
                            !previewUrl && /*#__PURE__*/ _jsx(TextSmall, {
                                children: "선택된 파일 없음"
                            })
                        ]
                    }),
                    previewUrl && /*#__PURE__*/ _jsx(ThumbnailPreview, {
                        src: previewUrl,
                        alt: "Thumbnail Preview"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SubmitButton, {
                type: "submit",
                disabled: isLoading,
                children: isLoading ? '저장 중...' : initialData ? '변경사항 저장' : '강의 생성'
            })
        ]
    });
};
export default CourseEditForm;
